import { FUELS } from 'Config';
import { DashboardGroup, DashboardWidgetLayout, WidgetCategory, WidgetDefinition } from './types/DashboardLayoutTypes';
export { CATEGORY_CONFIG } from './categoryConfig';

import { KpiCardsActivity, KpiCardsCosts } from './kpiCards/KpiCards';
import ExpensesChart from './expensesChart/ExpensesChart';
import GpsMapWidget from './gpsMapWidget/GpsMapWidget';
import ContractedKmAlerts from './contractedKmAlerts/ContractedKmAlerts';
import TopMaintenanceCosts from './topMaintenanceCosts/TopMaintenanceCosts';
import MonthlyCostChartBox from './monthlyCostChart/MonthlyCostChartBox';

import TopCostPerKm from './topCostPerKm/TopCostPerKm';
import TopMaintenanceTags from './topMaintenanceTags/TopMaintenanceTags';
import Co2Emissions from './co2Emissions/Co2Emissions';
import Co2MonthlyEvolution from './co2MonthlyEvolution/Co2MonthlyEvolution';
import EcoDrivingRanking from './ecoDrivingRanking/EcoDrivingRanking';
import DriverEcoComparison from './driverEcoComparison/DriverEcoComparison';
import EcoDistribution from './ecoDistribution/EcoDistribution';
import EcoEvolution from './ecoEvolution/EcoEvolution';
import ConsumesChartBox from './consumesChart/ConsumesChartBox';
import FuelExpensesPerAsset from './fuelExpensesPerAsset/FuelExpensesPerAsset';
import FuelConsumptionCombustionWidget from './fuelConsumptionPerAsset/FuelConsumptionCombustionWidget';
import ElectricConsumptionPerAssetWidget from './fuelConsumptionPerAsset/ElectricConsumptionPerAssetWidget';

import UpcomingReminders from './upcomingReminders/UpcomingReminders';
import ExpiredReminders from './expiredReminders/ExpiredReminders';
import OperationalKpis from './operationalKpis/OperationalKpis';
import UpcomingReservations from './upcomingReservations/UpcomingReservations';
import TopReservationVehicles from './topReservationVehicles/TopReservationVehicles';
import VehicleComparison from './vehicleComparison/VehicleComparison';
import TopMaintenanceComponents from './topMaintenanceComponents/TopMaintenanceComponents';
import ScheduledMaintenances from './scheduledMaintenances/ScheduledMaintenances';
import OverdueMaintenances from './overdueMaintenances/OverdueMaintenances';
import MaintenanceCalendar from './maintenanceCalendar/MaintenanceCalendar';
import MaintenanceCostEvolution from './maintenanceCostEvolution/MaintenanceCostEvolution';
import MonthlyRouteChartBox from './monthlyRouteChart/MonthlyRouteChartBox';
import MileageKpiIndicators from './mileageKpiIndicators/MileageKpiIndicators';
import MonthlyKmRanking from './monthlyKmRanking/MonthlyKmRanking';
import VehiclesByFuel from './vehiclesByFuel/VehiclesByFuel';
import AssetsAboveConsumption from './assetsAboveConsumption/AssetsAboveConsumption';
import VehiclesDriverAssignment from './vehiclesDriverAssignment/VehiclesDriverAssignment';
import VehiclesByAgency from './vehiclesByAgency/VehiclesByAgency';
import VehiclesBySegment from './vehiclesBySegment/VehiclesBySegment';
import MonthlyExpensesEvolution from './monthlyExpensesEvolution/MonthlyExpensesEvolution';
import TopVehicleCosts from './topVehicleCosts/TopVehicleCosts';
import TopDriverCosts from './topDriverCosts/TopDriverCosts';
import ContractsExpiring from './contractsExpiring/ContractsExpiring';
import ContractsExpired from './contractsExpired/ContractsExpired';
import ContractsDistribution from './contractsDistribution/ContractsDistribution';
import ContractsEvolution from './contractsEvolution/ContractsEvolution';
import DriverVehicleHeatmap from './driverVehicleHeatmap/DriverVehicleHeatmap';
import TopAppropriatedVehicles from './topAppropriatedVehicles/TopAppropriatedVehicles';
import DriverLoyaltyTable from './driverLoyaltyTable/DriverLoyaltyTable';
import LocationAlarms from './locationAlarms/LocationAlarms';
import CostCenterExpenses from './costCenterExpenses/CostCenterExpenses';
import CostCenterVisits from './costCenterVisits/CostCenterVisits';
import FuelTankTimeline from './fuelTankTimeline/FuelTankTimeline';

export const WIDGET_REGISTRY: Record<string, WidgetDefinition> = {
    // ── FLEET ──
    kpiCardsActivity: {
        id: 'kpiCardsActivity',
        titleKey: 'dashboard.fleet_counters',
        descriptionKey: 'dashboard.fleet_counters_desc',
        category: 'fleet',
        icon: '🚗',
        sizeLabel: 'full',
        chartPreview: 'kpi',
        component: KpiCardsActivity,
        defaultW: 12, defaultH: 2,
        minW: 3, minH: 2,
    },
    vehiclesByFuel: {
        id: 'vehiclesByFuel',
        titleKey: 'dashboard.vehicles_by_fuel',
        descriptionKey: 'dashboard.vehicles_by_fuel_desc',
        category: 'fleet',
        icon: '⛽',
        sizeLabel: 'medium',
        chartPreview: 'progress',
        component: VehiclesByFuel,
        defaultW: 3, defaultH: 4,
        minW: 3, minH: 3,
    },
    assetsAboveConsumption: {
        id: 'assetsAboveConsumption',
        titleKey: 'dashboard.assets_above_consumption',
        descriptionKey: 'dashboard.assets_above_consumption_desc',
        category: 'fleet',
        icon: '⛽',
        sizeLabel: 'medium',
        chartPreview: 'table',
        component: AssetsAboveConsumption,
        defaultW: 6, defaultH: 4,
        minW: 4, minH: 3,
    },
    fuelConsumptionPerAsset: {
        id: 'fuelConsumptionPerAsset',
        titleKey: 'dashboard.fuel_consumption_per_asset',
        assetTitleKey: 'dashboard.fuel_consumption_asset',
        descriptionKey: 'dashboard.fuel_consumption_per_asset_desc',
        assetDescriptionKey: 'dashboard.fuel_consumption_asset_desc',
        category: 'fleet',
        icon: '⛽',
        sizeLabel: 'medium',
        chartPreview: 'lineFuel',
        component: FuelConsumptionCombustionWidget,
        defaultW: 6, defaultH: 4,
        minW: 4, minH: 3,
    },
    electricConsumptionPerAsset: {
        id: 'electricConsumptionPerAsset',
        titleKey: 'dashboard.electric_consumption_per_asset',
        assetTitleKey: 'dashboard.electric_consumption_asset',
        descriptionKey: 'dashboard.electric_consumption_per_asset_desc',
        assetDescriptionKey: 'dashboard.electric_consumption_asset_desc',
        category: 'fleet',
        icon: '🔌',
        sizeLabel: 'medium',
        chartPreview: 'lineElectric',
        component: ElectricConsumptionPerAssetWidget,
        defaultW: 6, defaultH: 4,
        minW: 4, minH: 3,
    },
    // #66079 — Oscilação de combustível (FMC150). SÓ no dashboard do ativo (assetOnly).
    fuelTankTimeline: {
        id: 'fuelTankTimeline',
        titleKey: 'dashboard.fuel_tank_timeline',
        descriptionKey: 'dashboard.fuel_tank_timeline_desc',
        category: 'fleet',
        icon: '⛽',
        sizeLabel: 'full',
        chartPreview: 'line',
        component: FuelTankTimeline,
        defaultW: 12, defaultH: 4,
        minW: 4, minH: 3,
        assetOnly: true,
        addHidden: true,
    },
    vehiclesDriverAssignment: {
        id: 'vehiclesDriverAssignment',
        titleKey: 'dashboard.vehicles_driver_assignment',
        descriptionKey: 'dashboard.vehicles_driver_assignment_desc',
        category: 'fleet',
        icon: '👤',
        sizeLabel: 'medium',
        chartPreview: 'splitbar',
        component: VehiclesDriverAssignment,
        defaultW: 3, defaultH: 4,
        minW: 3, minH: 3,
    },
    vehiclesByAgency: {
        id: 'vehiclesByAgency',
        titleKey: 'dashboard.vehicles_by_agency',
        descriptionKey: 'dashboard.vehicles_by_agency_desc',
        category: 'fleet',
        icon: '🏢',
        sizeLabel: 'medium',
        chartPreview: 'hbar',
        component: VehiclesByAgency,
        defaultW: 3, defaultH: 4,
        minW: 3, minH: 3,
    },
    vehiclesBySegment: {
        id: 'vehiclesBySegment',
        titleKey: 'dashboard.vehicles_by_segment',
        descriptionKey: 'dashboard.vehicles_by_segment_desc',
        category: 'fleet',
        icon: '🏗️',
        sizeLabel: 'medium',
        chartPreview: 'donut',
        component: VehiclesBySegment,
        defaultW: 3, defaultH: 4,
        minW: 2, minH: 3,
    },

    // ── FINANCIAL ──
    // Row 1: Indicadores financeiros (12)
    kpiCardsCosts: {
        id: 'kpiCardsCosts',
        titleKey: 'dashboard.kpi_cards_costs',
        descriptionKey: 'dashboard.kpi_cards_costs_desc',
        assetDescriptionKey: 'dashboard.kpi_cards_costs_asset_desc',
        category: 'financial',
        icon: '💶',
        sizeLabel: 'full',
        chartPreview: 'kpi',
        component: KpiCardsCosts,
        defaultW: 12, defaultH: 2,
        minW: 3, minH: 2,
    },
    // Row 2: Distribuição despesa (4) + Ranking ativos caros (3) + Despesas fixas vs variáveis (5)
    expensesChart: {
        id: 'expensesChart',
        titleKey: 'dashboard.expenses',
        descriptionKey: 'dashboard.expenses_desc',
        category: 'financial',
        icon: '💰',
        sizeLabel: 'medium',
        chartPreview: 'donut',
        component: ExpensesChart,
        defaultW: 4, defaultH: 4,
        minW: 2, minH: 3,
    },
    topVehicleCosts: {
        id: 'topVehicleCosts',
        titleKey: 'dashboard.top_vehicle_costs',
        descriptionKey: 'dashboard.top_vehicle_costs_desc',
        category: 'financial',
        icon: '🏆',
        sizeLabel: 'medium',
        chartPreview: 'hbar',
        component: TopVehicleCosts,
        defaultW: 3, defaultH: 4,
        minW: 3, minH: 3,
    },
    monthlyCostChart: {
        id: 'monthlyCostChart',
        titleKey: 'dashboard.monthly_cost',
        descriptionKey: 'dashboard.monthly_cost_desc',
        category: 'financial',
        icon: '📊',
        sizeLabel: 'full',
        chartPreview: 'stackedDual',
        component: MonthlyCostChartBox,
        defaultW: 5, defaultH: 4,
        minW: 3, minH: 3,
    },
    // Row 3: Evolução mensal despesas (5) + Ranking €/km (3) + Consumos combustível (4)
    monthlyExpensesEvolution: {
        id: 'monthlyExpensesEvolution',
        titleKey: 'dashboard.monthly_expenses_evolution',
        descriptionKey: 'dashboard.monthly_expenses_evolution_desc',
        category: 'financial',
        icon: '📈',
        sizeLabel: 'medium',
        chartPreview: 'bar',
        component: MonthlyExpensesEvolution,
        defaultW: 5, defaultH: 4,
        minW: 3, minH: 3,
    },
    topCostPerKm: {
        id: 'topCostPerKm',
        titleKey: 'dashboard.top_cost_per_km',
        descriptionKey: 'dashboard.top_cost_per_km_desc',
        category: 'financial',
        icon: '📏',
        sizeLabel: 'medium',
        chartPreview: 'hbar',
        component: TopCostPerKm,
        defaultW: 3, defaultH: 4,
        minW: 3, minH: 3,
    },
    consumesChart: {
        id: 'consumesChart',
        titleKey: 'dashboard.consume',
        descriptionKey: 'dashboard.consume_desc',
        category: 'financial',
        icon: '⛽',
        sizeLabel: 'medium',
        chartPreview: 'line',
        component: ConsumesChartBox,
        defaultW: 4, defaultH: 4,
        minW: 3, minH: 3,
    },
    fuelExpensesPerAsset: {
        id: 'fuelExpensesPerAsset',
        titleKey: 'dashboard.fuel_expenses_per_asset',
        assetTitleKey: 'dashboard.fuel_expenses_asset',
        descriptionKey: 'dashboard.fuel_expenses_per_asset_desc',
        assetDescriptionKey: 'dashboard.fuel_expenses_asset_desc',
        category: 'financial',
        icon: '⛽',
        sizeLabel: 'medium',
        chartPreview: 'line',
        component: FuelExpensesPerAsset,
        defaultW: 6, defaultH: 4,
        minW: 4, minH: 3,
    },
    // Row 4: Ranking condutores (4) + Comparar Ativos (8)
    topDriverCosts: {
        id: 'topDriverCosts',
        titleKey: 'dashboard.top_driver_costs',
        descriptionKey: 'dashboard.top_driver_costs_desc',
        category: 'financial',
        icon: '👥',
        sizeLabel: 'medium',
        chartPreview: 'hbar',
        component: TopDriverCosts,
        defaultW: 4, defaultH: 4,
        minW: 3, minH: 3,
    },
    vehicleComparison: {
        id: 'vehicleComparison',
        titleKey: 'dashboard.vehicle_comparison',
        descriptionKey: 'dashboard.vehicle_comparison_desc',
        category: 'financial',
        icon: '📊',
        sizeLabel: 'full',
        chartPreview: 'compare',
        component: VehicleComparison,
        defaultW: 8, defaultH: 4,
        minW: 3, minH: 3,
    },

    // ── MILEAGE ──
    mileageKpiIndicators: {
        id: 'mileageKpiIndicators',
        titleKey: 'dashboard.mileage_kpi_indicators',
        descriptionKey: 'dashboard.mileage_kpi_indicators_desc',
        category: 'mileage',
        icon: '📍',
        sizeLabel: 'full',
        chartPreview: 'kpi',
        component: MileageKpiIndicators,
        defaultW: 12, defaultH: 2,
        minW: 3, minH: 2,
    },
    contractedKmAlerts: {
        id: 'contractedKmAlerts',
        titleKey: 'dashboard.contracted_km_vs_actual',
        descriptionKey: 'dashboard.contracted_km_vs_actual_desc',
        assetDescriptionKey: 'dashboard.contracted_km_vs_actual_asset_desc',
        category: 'mileage',
        icon: '🛣️',
        sizeLabel: 'full',
        chartPreview: 'progress',
        component: ContractedKmAlerts,
        defaultW: 6, defaultH: 4,
        minW: 3, minH: 4,
    },
    monthlyKmRanking: {
        id: 'monthlyKmRanking',
        titleKey: 'dashboard.monthly_km_ranking',
        descriptionKey: 'dashboard.monthly_km_ranking_desc',
        category: 'mileage',
        icon: '📈',
        sizeLabel: 'medium',
        chartPreview: 'hbar',
        component: MonthlyKmRanking,
        defaultW: 3, defaultH: 4,
        minW: 3, minH: 3,
        policyRequired: 'SETTINGUP_GPS_KMS_READ',
    },
    // ── CONTRACTS ──
    // Row 1: Distribuição por tipo (4) + Contratos a expirar (8)
    contractsDistribution: {
        id: 'contractsDistribution',
        titleKey: 'dashboard.contracts_distribution',
        descriptionKey: 'dashboard.contracts_distribution_desc',
        category: 'contracts',
        icon: '📄',
        sizeLabel: 'medium',
        chartPreview: 'donut',
        component: ContractsDistribution,
        defaultW: 4, defaultH: 4,
        minW: 2, minH: 3,
    },
    contractsExpiring: {
        id: 'contractsExpiring',
        titleKey: 'dashboard.contracts_expiring',
        descriptionKey: 'dashboard.contracts_expiring_desc',
        category: 'contracts',
        icon: '📅',
        sizeLabel: 'full',
        chartPreview: 'table',
        component: ContractsExpiring,
        defaultW: 8, defaultH: 4,
        minW: 3, minH: 3,
    },
    // Row 2: Contratos vencidos (7) + Evolução mensal custos (5)
    contractsExpired: {
        id: 'contractsExpired',
        titleKey: 'dashboard.contracts_expired',
        descriptionKey: 'dashboard.contracts_expired_desc',
        category: 'contracts',
        icon: '🔴',
        sizeLabel: 'full',
        chartPreview: 'table',
        component: ContractsExpired,
        defaultW: 7, defaultH: 4,
        minW: 3, minH: 3,
    },
    contractsEvolution: {
        id: 'contractsEvolution',
        titleKey: 'dashboard.contracts_evolution',
        descriptionKey: 'dashboard.contracts_evolution_desc',
        category: 'contracts',
        icon: '📈',
        sizeLabel: 'medium',
        chartPreview: 'line',
        component: ContractsEvolution,
        defaultW: 5, defaultH: 4,
        minW: 3, minH: 3,
    },

    // ── MAINTENANCE ──
    topMaintenanceCosts: {
        id: 'topMaintenanceCosts',
        titleKey: 'dashboard.top_maintenance_costs',
        descriptionKey: 'dashboard.top_maintenance_costs_desc',
        category: 'maintenance',
        icon: '🔧',
        sizeLabel: 'medium',
        chartPreview: 'hbar',
        component: TopMaintenanceCosts,
        defaultW: 3, defaultH: 4,
        minW: 3, minH: 3,
    },
    topMaintenanceTags: {
        id: 'topMaintenanceTags',
        titleKey: 'dashboard.top_maintenance_tags',
        descriptionKey: 'dashboard.top_maintenance_tags_desc',
        category: 'maintenance',
        icon: '🏷️',
        sizeLabel: 'medium',
        chartPreview: 'donut',
        component: TopMaintenanceTags,
        defaultW: 3, defaultH: 4,
        minW: 3, minH: 3,
    },
    topMaintenanceComponents: {
        id: 'topMaintenanceComponents',
        titleKey: 'dashboard.top_maintenance_components',
        descriptionKey: 'dashboard.top_maintenance_components_desc',
        category: 'maintenance',
        icon: '🔩',
        sizeLabel: 'medium',
        chartPreview: 'hbar',
        component: TopMaintenanceComponents,
        defaultW: 3, defaultH: 4,
        minW: 3, minH: 3,
    },
    scheduledMaintenances: {
        id: 'scheduledMaintenances',
        titleKey: 'dashboard.scheduled_maintenances',
        descriptionKey: 'dashboard.scheduled_maintenances_desc',
        category: 'maintenance',
        icon: '🔧',
        sizeLabel: 'full',
        chartPreview: 'table',
        component: ScheduledMaintenances,
        defaultW: 6, defaultH: 4,
        minW: 3, minH: 3,
    },
    overdueMaintenances: {
        id: 'overdueMaintenances',
        titleKey: 'dashboard.overdue_maintenances',
        descriptionKey: 'dashboard.overdue_maintenances_desc',
        category: 'maintenance',
        icon: '🔴',
        sizeLabel: 'full',
        chartPreview: 'table',
        component: OverdueMaintenances,
        defaultW: 6, defaultH: 4,
        minW: 3, minH: 3,
    },
    maintenanceCalendar: {
        id: 'maintenanceCalendar',
        titleKey: 'dashboard.maintenance_calendar',
        descriptionKey: 'dashboard.maintenance_calendar_desc',
        category: 'maintenance',
        icon: '📅',
        sizeLabel: 'medium',
        chartPreview: 'calendar',
        component: MaintenanceCalendar,
        defaultW: 3, defaultH: 4,
        minW: 3, minH: 3,
    },
    maintenanceCostEvolution: {
        id: 'maintenanceCostEvolution',
        titleKey: 'dashboard.maintenance_cost_evolution',
        descriptionKey: 'dashboard.maintenance_cost_evolution_desc',
        assetDescriptionKey: 'dashboard.maintenance_cost_evolution_asset_desc',
        category: 'maintenance',
        icon: '📈',
        sizeLabel: 'medium',
        chartPreview: 'line',
        component: MaintenanceCostEvolution,
        defaultW: 6, defaultH: 4,
        minW: 3, minH: 3,
    },

    // ── OPERATIONAL ──
    operationalKpis: {
        id: 'operationalKpis',
        titleKey: 'dashboard.operational_kpis',
        descriptionKey: 'dashboard.operational_kpis_desc',
        category: 'operational',
        icon: '✅',
        sizeLabel: 'full',
        chartPreview: 'kpi',
        component: OperationalKpis,
        defaultW: 12, defaultH: 2,
        minW: 3, minH: 2,
    },
    upcomingReservations: {
        id: 'upcomingReservations',
        titleKey: 'dashboard.upcoming_reservations.title',
        descriptionKey: 'dashboard.upcoming_reservations.desc',
        category: 'operational',
        icon: '📅',
        sizeLabel: 'medium',
        chartPreview: 'table',
        component: UpcomingReservations,
        defaultW: 7, defaultH: 4,
        minW: 3, minH: 3,
    },
    topReservationVehicles: {
        id: 'topReservationVehicles',
        titleKey: 'dashboard.top_reservation_vehicles.title',
        descriptionKey: 'dashboard.top_reservation_vehicles.desc',
        category: 'operational',
        icon: '🏆',
        sizeLabel: 'medium',
        chartPreview: 'hbar',
        component: TopReservationVehicles,
        defaultW: 5, defaultH: 4,
        minW: 3, minH: 3,
    },
    upcomingReminders: {
        id: 'upcomingReminders',
        titleKey: 'dashboard.upcoming_reminders',
        descriptionKey: 'dashboard.upcoming_reminders_desc',
        category: 'operational',
        icon: '🔔',
        sizeLabel: 'medium',
        chartPreview: 'table',
        component: UpcomingReminders,
        defaultW: 6, defaultH: 4,
        minW: 3, minH: 3,
    },
    expiredReminders: {
        id: 'expiredReminders',
        titleKey: 'dashboard.expired_reminders.title',
        descriptionKey: 'dashboard.expired_reminders.desc',
        category: 'operational',
        icon: '🔕',
        sizeLabel: 'medium',
        chartPreview: 'table',
        component: ExpiredReminders,
        defaultW: 6, defaultH: 4,
        minW: 3, minH: 3,
    },
    // ── Driver-Vehicle Preference section ──
    driverVehicleHeatmap: {
        id: 'driverVehicleHeatmap',
        titleKey: 'dashboard.driver_vehicle_heatmap.title',
        descriptionKey: 'dashboard.driver_vehicle_heatmap.desc',
        category: 'operational',
        icon: '🔥',
        sizeLabel: 'medium',
        chartPreview: 'calendar',
        component: DriverVehicleHeatmap,
        defaultW: 6, defaultH: 5,
        minW: 4, minH: 4,
    },
    topAppropriatedVehicles: {
        id: 'topAppropriatedVehicles',
        titleKey: 'dashboard.top_appropriated_vehicles.title',
        descriptionKey: 'dashboard.top_appropriated_vehicles.desc',
        category: 'operational',
        icon: '🚘',
        sizeLabel: 'medium',
        chartPreview: 'hbar',
        component: TopAppropriatedVehicles,
        defaultW: 6, defaultH: 4,
        minW: 3, minH: 3,
    },
    driverLoyaltyTable: {
        id: 'driverLoyaltyTable',
        titleKey: 'dashboard.driver_loyalty_table.title',
        descriptionKey: 'dashboard.driver_loyalty_table.desc',
        category: 'operational',
        icon: '📋',
        sizeLabel: 'full',
        chartPreview: 'table',
        component: DriverLoyaltyTable,
        defaultW: 12, defaultH: 5,
        minW: 6, minH: 4,
    },

    // ── LOCATION ──
    gpsMapWidget: {
        id: 'gpsMapWidget',
        titleKey: 'dashboard.gps_map',
        assetTitleKey: 'dashboard.gps_map_vehicle',
        descriptionKey: 'dashboard.gps_map_desc',
        assetDescriptionKey: 'dashboard.gps_map_asset_desc',
        category: 'location',
        icon: '🗺️',
        sizeLabel: 'medium',
        chartPreview: 'map',
        component: GpsMapWidget,
        defaultW: 3, defaultH: 4,
        minW: 3, minH: 3,
        policyRequired: 'SETTINGUP_GPS_READ',
    },
    monthlyRouteChart: {
        id: 'monthlyRouteChart',
        titleKey: 'dashboard.monthly_routes',
        assetTitleKey: 'dashboard.monthly_routes_vehicle',
        descriptionKey: 'dashboard.monthly_routes_desc',
        category: 'location',
        icon: '📍',
        sizeLabel: 'medium',
        chartPreview: 'line',
        component: MonthlyRouteChartBox,
        defaultW: 5, defaultH: 4,
        minW: 3, minH: 3,
        policyRequired: 'SETTINGUP_GPS_KMS_READ',
    },
    costCenterExpenses: {
        id: 'costCenterExpenses',
        titleKey: 'dashboard.cost_center_expenses',
        descriptionKey: 'dashboard.cost_center_expenses_desc',
        category: 'location',
        icon: '💶',
        sizeLabel: 'medium',
        chartPreview: 'hbar',
        component: CostCenterExpenses,
        defaultW: 4, defaultH: 4,
        minW: 3, minH: 3,
        policyRequired: 'SETTINGUP_COST_CENTER_READ',
    },
    locationAlarms: {
        id: 'locationAlarms',
        titleKey: 'dashboard.location_alarms',
        descriptionKey: 'dashboard.location_alarms_desc',
        category: 'location',
        icon: '🚨',
        sizeLabel: 'full',
        chartPreview: 'table',
        component: LocationAlarms,
        defaultW: 8, defaultH: 5,
        minW: 6, minH: 4,
        policyRequired: 'SETTINGUP_GPS_READ',
    },
    costCenterVisits: {
        id: 'costCenterVisits',
        titleKey: 'dashboard.cost_center_visits',
        descriptionKey: 'dashboard.cost_center_visits_desc',
        assetDescriptionKey: 'dashboard.cost_center_visits_asset_desc',
        category: 'location',
        icon: '⏱️',
        sizeLabel: 'medium',
        chartPreview: 'hbar',
        component: CostCenterVisits,
        defaultW: 4, defaultH: 5,
        minW: 3, minH: 3,
        policyRequired: 'SETTINGUP_COST_CENTER_READ',
    },

    // ── SUSTAINABILITY ──
    co2Emissions: {
        id: 'co2Emissions',
        titleKey: 'dashboard.co2_emissions',
        descriptionKey: 'dashboard.co2_emissions_desc',
        category: 'sustainability',
        icon: '🌿',
        sizeLabel: 'medium',
        chartPreview: 'hbar',
        component: Co2Emissions,
        defaultW: 7, defaultH: 4,
        minW: 3, minH: 3,
    },
    co2MonthlyEvolution: {
        id: 'co2MonthlyEvolution',
        titleKey: 'dashboard.co2_monthly_evolution',
        descriptionKey: 'dashboard.co2_monthly_evolution_desc',
        assetDescriptionKey: 'dashboard.co2_monthly_evolution_asset_desc',
        category: 'sustainability',
        icon: '📈',
        sizeLabel: 'medium',
        chartPreview: 'line',
        component: Co2MonthlyEvolution,
        defaultW: 5, defaultH: 4,
        minW: 3, minH: 3,
    },
    ecoDrivingRanking: {
        id: 'ecoDrivingRanking',
        titleKey: 'dashboard.eco_driving_ranking',
        descriptionKey: 'dashboard.eco_driving_ranking_desc',
        category: 'sustainability',
        icon: '🏆',
        sizeLabel: 'full',
        chartPreview: 'bar',
        component: EcoDrivingRanking,
        defaultW: 12, defaultH: 4,
        minW: 6, minH: 3,
        policyRequired: 'SETTINGUP_ECO_DRIVING_READ',
        hiddenInGroups: ['RGPD'],
    },
    driverEcoComparison: {
        id: 'driverEcoComparison',
        titleKey: 'dashboard.driver_eco_comparison',
        descriptionKey: 'dashboard.driver_eco_comparison_desc',
        category: 'sustainability',
        icon: '📈',
        sizeLabel: 'full',
        chartPreview: 'line',
        component: DriverEcoComparison,
        defaultW: 8, defaultH: 4,
        minW: 4, minH: 3,
        policyRequired: 'SETTINGUP_ECO_DRIVING_READ',
        hiddenInGroups: ['RGPD'],
    },
    // Versões agregadas (RGPD-safe): sem hiddenInGroups → visíveis em FULL e RGPD.
    ecoDistribution: {
        id: 'ecoDistribution',
        titleKey: 'dashboard.eco_distribution',
        descriptionKey: 'dashboard.eco_distribution_desc',
        category: 'sustainability',
        icon: '♻️',
        sizeLabel: 'medium',
        chartPreview: 'donut',
        component: EcoDistribution,
        defaultW: 4, defaultH: 4,
        minW: 3, minH: 3,
        policyRequired: 'SETTINGUP_ECO_DRIVING_READ',
        addHidden: true,
    },
    ecoEvolution: {
        id: 'ecoEvolution',
        titleKey: 'dashboard.eco_evolution',
        descriptionKey: 'dashboard.eco_evolution_desc',
        assetDescriptionKey: 'dashboard.eco_evolution_asset_desc',
        category: 'sustainability',
        icon: '📈',
        sizeLabel: 'medium',
        chartPreview: 'line',
        component: EcoEvolution,
        defaultW: 8, defaultH: 4,
        minW: 4, minH: 3,
        policyRequired: 'SETTINGUP_ECO_DRIVING_READ',
        // No detalhe do ativo este gráfico fica scoped a um veículo (logo, identifica o condutor
        // do carro atribuído). A Norvia controla-o por uma política própria, separada da geral.
        assetPolicyRequired: 'SETTINGUP_ASSET_ECO_DRIVING_READ',
        addHidden: true,
    },
};

export const ENABLED_CATEGORIES: WidgetCategory[] = ['fleet', 'financial', 'mileage', 'contracts', 'maintenance', 'operational', 'location', 'sustainability'];

export const getEnabledCategoriesForUser = (userPolicies: string[]): WidgetCategory[] => {
    const hasGpsRead = (userPolicies || []).includes('SETTINGUP_GPS_READ');
    const hasCostCenterRead = (userPolicies || []).includes('SETTINGUP_COST_CENTER_READ');
    const canSeeLocation = hasGpsRead || hasCostCenterRead;
    return ENABLED_CATEGORIES.filter(cat => {
        if (cat === 'location') return canSeeLocation;
        return true;
    });
};

// Política efetiva do widget: no contexto do ativo, `assetPolicyRequired` (se existir) tem
// precedência sobre `policyRequired`. Fonte única para render (DashboardCanvas) e picker.
export const effectivePolicyRequired = (def: WidgetDefinition, isAssetContext = false): string | undefined =>
    (isAssetContext && def.assetPolicyRequired) ? def.assetPolicyRequired : def.policyRequired;

export const canAccessWidget = (widgetId: string, userPolicies: string[], isAssetContext = false): boolean => {
    const def = WIDGET_REGISTRY[widgetId];
    if (!def) return false;
    const policy = effectivePolicyRequired(def, isAssetContext);
    if (!policy) return true;
    return (userPolicies || []).includes(policy);
};

// Grupo de dashboards da empresa (gestor da plataforma). Um widget pertence ao grupo
// se não estiver listado no seu `hiddenInGroups`. FULL vê tudo; RGPD esconde os identificáveis.
export const isWidgetInGroup = (widgetId: string, group: DashboardGroup): boolean => {
    const def = WIDGET_REGISTRY[widgetId];
    if (!def) return false;
    return !def.hiddenInGroups?.includes(group);
};

// Widgets escondidos num dado grupo (ex.: usado no modal de comparação na ficha da empresa).
export const getWidgetsHiddenInGroup = (group: DashboardGroup): WidgetDefinition[] =>
    Object.values(WIDGET_REGISTRY).filter(def => def.hiddenInGroups?.includes(group));

export const DEFAULT_LAYOUT: DashboardWidgetLayout[] = Object.keys(WIDGET_REGISTRY).map((id, index) => {
    const def = WIDGET_REGISTRY[id];
    return {
        i: id,
        x: 0,
        y: index,
        w: def.defaultW,
        h: def.defaultH,
        minW: def.minW,
        minH: def.minH,
        visible: false,
    };
});

export const LAYOUT_VERSION = 35;

// ─────────────────────────────────────────────────────────────────────────────
//  Asset (single-vehicle) dashboard
//
//  O detalhe do ativo reutiliza a mesma engine de grelha/picker do painel
//  principal, mas vocacionado a UM ativo: todos os dados filtrados pelo
//  vehicleId. Só um subconjunto curado de widgets faz sentido para um ativo
//  (agregados/rankings de frota ficam de fora). Os títulos seguem o template
//  entregue pelo cliente (pagina-ativo.html).
// ─────────────────────────────────────────────────────────────────────────────

export const ASSET_LAYOUT_VERSION = 1;

// Catálogo de widgets disponíveis no dashboard do ativo, com a posição preferida
// (secção como no template) para quando o utilizador os adiciona pelo picker.
// NOTA: este array define o ALLOWLIST (o que o picker deixa adicionar). Não controla
// o que está visível por defeito — isso é o ASSET_DEFAULT_VISIBLE_IDS abaixo.
const ASSET_WIDGET_CATALOG: Array<{ i: string; x: number; y: number; w: number; h: number }> = [
    // ── Consumo e quilometragem ──
    { i: 'fuelConsumptionPerAsset', x: 0, y: 0, w: 6, h: 4 },
    { i: 'electricConsumptionPerAsset', x: 6, y: 0, w: 6, h: 4 },
    { i: 'fuelTankTimeline', x: 0, y: 4, w: 12, h: 4 },
    { i: 'contractedKmAlerts', x: 0, y: 8, w: 6, h: 4 },
    { i: 'monthlyRouteChart', x: 6, y: 8, w: 6, h: 4 },
    // ── Custos ──
    { i: 'kpiCardsCosts', x: 0, y: 8, w: 12, h: 2 },
    { i: 'expensesChart', x: 0, y: 10, w: 4, h: 4 },
    { i: 'contractsDistribution', x: 4, y: 10, w: 4, h: 4 },
    { i: 'monthlyCostChart', x: 8, y: 10, w: 4, h: 4 },
    { i: 'fuelExpensesPerAsset', x: 0, y: 14, w: 6, h: 4 },
    { i: 'maintenanceCostEvolution', x: 6, y: 14, w: 6, h: 4 },
    // ── Sustentabilidade ──
    { i: 'co2MonthlyEvolution', x: 0, y: 18, w: 6, h: 4 },
    { i: 'ecoEvolution', x: 6, y: 18, w: 6, h: 4 },
    // ── Contratos ──
    { i: 'contractsExpiring', x: 0, y: 22, w: 6, h: 4 },
    { i: 'contractsExpired', x: 6, y: 22, w: 6, h: 4 },
    // ── Manutenções ──
    { i: 'maintenanceCalendar', x: 0, y: 26, w: 6, h: 4 },
    { i: 'topMaintenanceTags', x: 6, y: 26, w: 3, h: 4 },
    { i: 'topMaintenanceComponents', x: 9, y: 26, w: 3, h: 4 },
    { i: 'scheduledMaintenances', x: 0, y: 30, w: 6, h: 4 },
    { i: 'overdueMaintenances', x: 6, y: 30, w: 6, h: 4 },
    // ── Reservas e lembretes ──
    { i: 'upcomingReservations', x: 0, y: 34, w: 6, h: 4 },
    { i: 'upcomingReminders', x: 6, y: 34, w: 6, h: 4 },
    { i: 'expiredReminders', x: 0, y: 38, w: 6, h: 4 },
    // ── Localização ──
    { i: 'gpsMapWidget', x: 0, y: 42, w: 6, h: 4 },
    { i: 'locationAlarms', x: 6, y: 42, w: 6, h: 5 },
    { i: 'costCenterVisits', x: 0, y: 47, w: 6, h: 5 },
    { i: 'costCenterExpenses', x: 6, y: 47, w: 6, h: 4 },
];

// Widgets adicionais disponíveis no picker do ativo (fora do catálogo posicionado).
const ASSET_EXTRA_WIDGET_IDS = ['consumesChart', 'monthlyExpensesEvolution', 'contractsEvolution'];

// Conjunto de widgets que PODEM aparecer no dashboard do ativo (catálogo + extras).
export const ASSET_WIDGET_IDS = new Set<string>([
    ...ASSET_WIDGET_CATALOG.map(w => w.i),
    ...ASSET_EXTRA_WIDGET_IDS,
]);

// Widgets do ativo que dependem de capacidades do veículo — usados para o gate do picker
// (CarDashboardTab) E para o popover de incompatibilidade na replicação. TÊM de ficar em sincronia.
export const ASSET_GPS_DEPENDENT_WIDGET_IDS = ['monthlyRouteChart', 'gpsMapWidget', 'locationAlarms', 'costCenterVisits', 'costCenterExpenses', 'ecoEvolution', 'fuelTankTimeline'];
export const ASSET_RESERVATION_WIDGET_IDS = ['upcomingReservations'];

// Capacidades de um ativo que condicionam que widgets fazem sentido nele. Quando uma
// capacidade não é conhecida (undefined → falsy), o respetivo gate é aplicado como "não tem".
export interface AssetCapabilities {
    hasGps?: boolean;
    reservationAvailable?: boolean;
    hasKmFeature?: boolean;
    hasContractedKm?: boolean;
    // `fuelName` em VehicleFuelViewModel é opcional → aceita undefined nos elementos.
    fuelNames?: (string | undefined)[];
}

// Conjunto de widgets que um ativo COM estas capacidades consegue mostrar. Fonte única da
// curadoria por capacidade — reusada pelo gate do picker (CarDashboardTab) E pela sanitização
// de "Copiar de"/"Replicar para" (não revelar no destino widgets que a origem não mostra).
export const computeAssetAvailableWidgetIds = (caps: AssetCapabilities): Set<string> => {
    const ids = new Set(ASSET_WIDGET_IDS);
    // Sem dispositivo de tracking → esconder tudo o que depende de GPS/telemetria.
    if (!caps.hasGps) ASSET_GPS_DEPENDENT_WIDGET_IDS.forEach(id => ids.delete(id));
    // Não disponível para reserva → "Próximas reservas".
    if (!caps.reservationAvailable) ASSET_RESERVATION_WIDGET_IDS.forEach(id => ids.delete(id));
    // Sem contrato com km contratual (ex.: só compra) → "Km atuais vs contratuais".
    if (!caps.hasContractedKm) ids.delete('contractedKmAlerts');

    const fuelNames = caps.fuelNames ?? [];
    const hasElectric = fuelNames.includes(FUELS.ELECTRIC);
    const hasCombustion = fuelNames.some(n => !!n && n !== FUELS.ELECTRIC);
    // "Despesas por tipo de combustível" exige feature de KM e mais do que 1 combustível
    // (com 1 só é redundante com "Despesas de combustível por ativo").
    if (!caps.hasKmFeature || fuelNames.length < 2) ids.delete('consumesChart');
    if (!hasElectric) ids.delete('electricConsumptionPerAsset');
    if (!hasCombustion) ids.delete('fuelConsumptionPerAsset');

    return ids;
};

// Força visible:false em widgets fora do conjunto permitido (mantém posição/tamanho).
// Usado para tornar "honesta" uma disposição antes de a atravessar para outro ativo.
export const sanitizeVisibility = (
    widgets: DashboardWidgetLayout[],
    allowed: Set<string>,
): DashboardWidgetLayout[] => widgets.map(w => ({ ...w, visible: w.visible && allowed.has(w.i) }));

// Título a mostrar para um widget: no contexto do ativo usa `assetTitleKey` (ex.: "do ativo"
// em vez de "da frota") quando existe. Usado no picker e no popover de replicação.
export const widgetTitleKey = (def: WidgetDefinition, assetScope: boolean) =>
    (assetScope && def.assetTitleKey) ? def.assetTitleKey : def.titleKey;

export const widgetDescriptionKey = (def: WidgetDefinition, assetScope: boolean) =>
    (assetScope && def.assetDescriptionKey) ? def.assetDescriptionKey : def.descriptionKey;

// Widgets visíveis ao abrir o dashboard de um ativo pela 1.ª vez (sem layout gravado).
// VAZIO de propósito: o cliente quer o dashboard do ativo a começar SEM widgets — o
// utilizador adiciona o que quiser pelo picker. Para pré-popular, listar IDs aqui
// (têm de existir no catálogo acima para herdarem a posição preferida).
const ASSET_DEFAULT_VISIBLE_IDS: string[] = [];

export const DEFAULT_ASSET_LAYOUT: DashboardWidgetLayout[] = (() => {
    const posMap = new Map(ASSET_WIDGET_CATALOG.map(w => [w.i, w]));
    const visibleSet = new Set(ASSET_DEFAULT_VISIBLE_IDS);
    let maxY = ASSET_WIDGET_CATALOG.reduce((max, w) => Math.max(max, w.y + w.h), 0);
    // Mantém a ordem do registry para determinismo; só widgets do allowlist.
    return Object.keys(WIDGET_REGISTRY)
        .filter(id => ASSET_WIDGET_IDS.has(id))
        .map(id => {
            const def = WIDGET_REGISTRY[id];
            const pos = posMap.get(id);
            if (visibleSet.has(id) && pos) {
                return { i: id, x: pos.x, y: pos.y, w: pos.w, h: pos.h, minW: def.minW, minH: def.minH, visible: true };
            }
            const item: DashboardWidgetLayout = {
                i: id, x: 0, y: maxY, w: def.defaultW, h: def.defaultH, minW: def.minW, minH: def.minH, visible: false,
            };
            maxY += def.defaultH;
            return item;
        });
})();
